import {
  index,
  integer,
  primaryKey,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { taxProfile } from "@/db/schema/tax";
import { hiive } from "./hiive";

export const BILLING_INTERVAL = [
  "month",
  "quarter",
  "half_year",
  "year",
  "lifetime",
] as const;
export const TIER_STATUS = ["active", "inactive", "archived"] as const;

export type BillingInterval = (typeof BILLING_INTERVAL)[number];
export type TierStatus = (typeof TIER_STATUS)[number];

// Tiers
export const hiiveTier = sqliteTable(
  "hiive_tier",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    status: text("status").notNull().$type<TierStatus>().default("active"),
    sortOrder: integer("sort_order").notNull().default(0),
    requiresApproval: integer("requires_approval", { mode: "boolean" })
      .notNull()
      .default(false),

    /**
     * Members of this tier appear as the tier, not as themselves.
     *
     * Discretion is the product for some rooms — a tier whose members would
     * not join if being seen were the price. Everywhere a member of an
     * anonymous tier is listed to somebody else, they render as
     * "<Tier> member" with no photo. It is a DISPLAY rule: the membership row
     * is unchanged, and staff surfaces (door lists, the queen's own member
     * table) still resolve real identities, because someone has to run the
     * event.
     */
    anonymousMembers: integer("anonymous_members", { mode: "boolean" })
      .notNull()
      .default(false),

    /**
     * Does this tier land in the hiive-wide General chat on approval?
     *
     * Default true, which is what every existing tier did unconditionally
     * before this column. Combined with `hiive_tier_chat_access` it expresses
     * all three options an organiser asked for without an enum that can
     * contradict itself: no chat = this off and no exclusive links; general =
     * this on; exclusive = this off and one or more links.
     */
    joinsGeneralChat: integer("joins_general_chat", { mode: "boolean" })
      .notNull()
      .default(true),

    description: text("description").notNull(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("hiive_tier_hiiveid_slug_unique").on(t.hiiveId, t.slug),
    index("hiive_tier_sort_idx").on(t.sortOrder),
  ],
);

// Prices (supports free/no-billing and paid subscription cadences)
export const hiiveTierPrice = sqliteTable(
  "hiive_tier_price",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    tierId: integer("tier_id")
      .notNull()
      .references(() => hiiveTier.id, { onDelete: "cascade" }),

    // cached pricing
    priceAmountMinor: integer("price_amount_minor").notNull(), // 0 = free
    currency: text("currency").notNull().default("INR"),

    // Tax. Null = untaxed. `priceIncludesTax` describes the stored price, which
    // is always the gross — the member is quoted one figure per period and that
    // is what Razorpay charges, so the tax has to live inside it.
    taxProfileId: integer("tax_profile_id").references(() => taxProfile.id, {
      onDelete: "set null",
    }),
    priceIncludesTax: integer("price_includes_tax", { mode: "boolean" })
      .notNull()
      .default(true),

    // recurrence
    billingInterval: text("billing_interval")
      .notNull()
      .$type<BillingInterval>() // paid: month | quarter | half_year | year; free: lifetime
      .default("lifetime"),

    // UI/ops
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),

    // Razorpay wiring
    razorpayPlanId: text("razorpay_plan_id"), // paid subscriptions

    ...timestamps,
  },
  (t) => [
    uniqueIndex("tier_price_unique").on(
      t.tierId,
      t.currency,
      t.billingInterval,
    ),
    index("tier_price_tier_active_sort_idx").on(
      t.tierId,
      t.isActive,
      t.sortOrder,
    ),
    index("tier_price_plan_idx").on(t.razorpayPlanId),
  ],
);

/**
 * Which tiers can see and post in an exclusive chat.
 *
 * A join table rather than a column on either side, because both directions
 * are many: one chat can admit several tiers ("Homies and Selectors share the
 * green room"), and one tier can sit in several chats. The chat row itself
 * carries the name, description and `isReadOnly` — this only answers who gets
 * let in.
 */
export const hiiveTierChatAccess = sqliteTable(
  "hiive_tier_chat_access",
  {
    chatId: integer("chat_id").notNull(),
    tierId: integer("tier_id")
      .notNull()
      .references(() => hiiveTier.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (t) => [
    primaryKey({
      name: "hiive_tier_chat_access_pk",
      columns: [t.chatId, t.tierId],
    }),
    index("hiive_tier_chat_access_by_tier").on(t.tierId),
    index("hiive_tier_chat_access_by_chat").on(t.chatId),
  ],
);
